import React, { useEffect, useState } from 'react';
import { ArrowUpRight, BatteryFull, CheckCheck, ChevronLeft, FileText, Image, Mic, Phone, Play, Plus, Reply, Signal, Video, Wifi } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext.tsx';
import { isWebUrl } from '../../utils/whatsappTemplateBuilder.ts';
import { getClinicInfo } from '../../services/api.ts';

export interface WhatsAppTemplatePreviewProps {
  clinicId: number | null;
  components: any[];
  bodyValues?: Record<string, string>;
  headerValues?: Record<string, string>;
  mediaUrl?: string;
  mediaName?: string;
  isDark?: boolean;
}

const replaceExamples = (text: string, examples: string[] = [], overrides: Record<string, string> = {}) =>
  text.replace(/\{\{([1-9]\d*)\}\}/g, (match, key) => overrides[key] || examples[Number(key) - 1] || match);

/** Non-interactive, illustrative preview: it never sends messages or opens template links. */
export function WhatsAppTemplatePreview({ clinicId, components, bodyValues, headerValues, mediaUrl, mediaName, isDark: darkOverride }: WhatsAppTemplatePreviewProps) {
  const { isDark: contextDark } = useTheme();
  const isDark = darkOverride ?? contextDark;
  const [identity, setIdentity] = useState<{ clinicId: number; name: string; logoUrl: string | null } | null>(null);
  const [failedLogoUrl, setFailedLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setIdentity(null);
    setFailedLogoUrl(null);
    if (!clinicId) return;
    // Use the authenticated Sminy clinic account, never the WhatsApp profile.
    void getClinicInfo().then(info => {
      if (active && Number(info.id) === clinicId) {
        setIdentity({ clinicId, name: info.name_clinic?.trim() || info.name?.trim() || 'Sua clínica', logoUrl: info.logo_url?.trim() || null });
      }
    }).catch(() => {
      // Account branding is illustrative; a failed lookup must not block editing.
    });
    return () => { active = false; };
  }, [clinicId]);

  const currentIdentity = identity?.clinicId === clinicId ? identity : null;
  const clinicName = currentIdentity?.name || 'Sua clínica';
  const logoUrl = currentIdentity?.logoUrl;
  const initials = clinicName.split(/\s+/).slice(0, 2).map(word => word[0]).join('').toUpperCase();
  const header = components.find(component => component.type === 'HEADER');
  const body = components.find(component => component.type === 'BODY');
  const footer = components.find(component => component.type === 'FOOTER');
  const buttons = components.find(component => component.type === 'BUTTONS')?.buttons || [];
  const sampleUrl = header?.example?.header_handle?.[0];
  const source = mediaUrl?.startsWith('blob:') || isWebUrl(mediaUrl || '') ? mediaUrl : isWebUrl(sampleUrl || '') ? sampleUrl : undefined;
  const advanced = components.some(component => !['HEADER', 'BODY', 'FOOTER', 'BUTTONS'].includes(component.type));
  const text = replaceExamples(body?.text || 'O conteúdo da sua mensagem aparecerá aqui.', body?.example?.body_text?.[0], bodyValues);
  const bubble = isDark ? 'bg-[#202C33] text-[#E9EDEF]' : 'bg-white text-[#111B21]';
  return (
    <div className="mx-auto w-full max-w-[344px]" aria-label="Prévia ilustrativa no WhatsApp">
      <div className={`relative overflow-hidden rounded-[46px] border-[8px] border-[#242628] shadow-sm ${isDark ? 'bg-[#0B141A]' : 'bg-[#F5F6F6]'}`}>
        <div className={`relative flex h-12 items-center justify-between px-6 pt-1 text-[11px] font-semibold ${isDark ? 'text-white' : 'text-[#111B21]'}`}>
          <span>9:41</span><span className="absolute left-1/2 top-3 h-6 w-24 -translate-x-1/2 rounded-full bg-black" />
          <span className="flex items-center gap-1"><Signal size={12} /><Wifi size={12} /><BatteryFull size={16} /></span>
        </div>
        <div className={`flex h-16 items-center gap-2 px-2 ${isDark ? 'text-[#E9EDEF]' : 'text-[#111B21]'}`}>
          <ChevronLeft size={23} className="shrink-0 text-sminy-primary" />
          <span className={`flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full text-xs font-semibold ${isDark ? 'bg-[#2A3942]' : 'bg-[#DFE5E7]'}`}>
            {logoUrl && logoUrl !== failedLogoUrl
              ? <img src={logoUrl} alt={`Foto de ${clinicName}`} className="h-full w-full object-cover" onError={() => setFailedLogoUrl(logoUrl)} />
              : initials}
          </span>
          <div className="min-w-0 flex-1"><p className="truncate text-xs font-medium">{clinicName}</p><p className="mt-1 text-[10px] text-[#8696A0]">Conta comercial</p></div>
          <Video size={18} className="text-sminy-primary" /><Phone size={16} className="mr-1 text-sminy-primary" />
        </div>
        <div className={`min-h-[430px] space-y-3 px-3 pb-8 pt-4 ${isDark ? 'bg-[#0B141A]' : 'bg-[#EFEAE2]'}`}>
          <div className="text-center"><span className={`inline-block rounded-lg px-3 py-1 text-[10px] ${bubble}`}>Hoje</span></div>
          <div className={`rounded-xl rounded-tl-none p-3 shadow-sm ${bubble}`}>
            {header?.format === 'TEXT' && <p className="mb-2 whitespace-pre-wrap break-words text-sm font-semibold">{replaceExamples(header.text || '', header.example?.header_text, headerValues)}</p>}
            {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(header?.format) && (
              <div className={`mb-3 overflow-hidden rounded-lg ${isDark ? 'bg-[#2A3942]' : 'bg-[#E8EEF0]'}`}>
                {header.format === 'IMAGE' && source ? <img src={source} alt="Imagem de exemplo do cabeçalho" className="max-h-48 w-full object-contain" />
                  : header.format === 'VIDEO' && source ? <video src={source} controls preload="metadata" className="max-h-48 w-full" aria-label="Vídeo de exemplo do cabeçalho" />
                    : <div className="flex min-h-28 flex-col items-center justify-center gap-2 p-4 text-[#8696A0]">
                      {header.format === 'IMAGE' ? <Image size={32} /> : header.format === 'VIDEO' ? <Play size={32} /> : <FileText size={32} />}
                      <span className="max-w-full truncate text-xs">{mediaName || ({ IMAGE: 'Imagem de exemplo', VIDEO: 'Vídeo de exemplo', DOCUMENT: 'Documento PDF' } as Record<string, string>)[header.format]}</span>
                    </div>}
              </div>
            )}
            <p className="whitespace-pre-wrap break-words text-[13px] leading-relaxed">{text}</p>
            {footer?.text && <p className="mt-4 break-words text-[10px] leading-relaxed text-[#8696A0]">{footer.text}</p>}
            <span className="mt-3 flex items-center justify-end gap-1 text-[9px] text-[#8696A0]">9:41 <CheckCheck size={13} /></span>
          </div>
          {buttons.map((button: any, index: number) => (
            <div key={index} className={`flex min-h-10 items-center justify-center gap-2 rounded-lg px-3 py-2 text-center text-xs ${bubble}`}>
              <span className="flex items-center justify-center gap-2 text-sminy-primary">
                {button.type === 'URL' ? <ArrowUpRight size={15} /> : button.type === 'PHONE_NUMBER' ? <Phone size={14} /> : <Reply size={15} />}
                {button.text || 'Texto do botão'}
              </span>
            </div>
          ))}
          {advanced && <p className="rounded-lg bg-amber-50 p-2 text-xs text-amber-800">Componentes avançados não são reproduzidos nesta prévia.</p>}
        </div>
        <div className={`flex h-14 items-center gap-2 px-3 ${isDark ? 'bg-[#202C33]' : 'bg-[#F5F6F6]'}`}>
          <Plus size={20} className="text-sminy-primary" /><div className={`flex-1 rounded-full px-3 py-2 text-[10px] text-[#8696A0] ${isDark ? 'bg-[#2A3942]' : 'bg-white'}`}>Mensagem</div><Mic size={17} className="text-sminy-primary" />
        </div>
        <div className="flex h-6 justify-center pt-2"><span className={`h-1 w-28 rounded-full ${isDark ? 'bg-white' : 'bg-[#111B21]'}`} /></div>
      </div>
      <p className={`mt-3 text-center text-[11px] leading-relaxed ${isDark ? 'text-slate-400' : 'text-sminy-muted'}`}>Prévia ilustrativa. A aparência pode variar no WhatsApp.</p>
    </div>
  );
}

export default WhatsAppTemplatePreview;
